import { PiBuildingsLight, PiGraduationCap } from "react-icons/pi";
import CustomBarChart from "../Charts/CustomBarChart";
import CommonCard from "../ui/CommonCard/CommonCard";
import classes from "./Dashboard.module.css";
import { BsBuilding } from "react-icons/bs";
import { LiaCertificateSolid } from "react-icons/lia";
import { AiOutlineSafetyCertificate } from "react-icons/ai";
import { FaArrowTrendUp, FaGraduationCap } from "react-icons/fa6";
import LabStatusChart from "../Charts/LabStatusChart";
import CommonButton from "../ui/CommonButton/CommonButton";
import { IoListOutline, IoLocationOutline } from "react-icons/io5";
import { HiOutlineDownload } from "react-icons/hi";
import { useEffect, useMemo, useState } from "react";
import { env } from "../../constants/env";
import axios from "axios";
import ReusableTable from "../ReusableTable/ReusableTable";
import { IoMdClose } from "react-icons/io";
import { GoShieldCheck, GoShieldX } from "react-icons/go";
import OverlayModal from "../ui/OverlayModal/OverlayModal";
import { useAuth } from "../../context/AuthContext";
import ZambiaLabsMap from "./ZambiaLabsMap";
import InputField from "../ui/InputField/InputField";
import LabStatusOverview from "./LabStatusOverview";
import SelectedLabDetails from "./SelectedLabDetails";
import { formatInputDate } from "../../utils/functions";
import { toast } from "react-toastify";
import { db } from "../../utils/offlineDB";

// 1) Status colors
const STATUS_COLORS = {
  Good: "#1302FB",
  Excellent: "#1CDF03",
  Fair: "#FE9900",
  Poor: "#E60101",
};

const CITY_COORDS = {
  // Central
  Kabwe: [28.4464, -14.4469],
  "Kapiri Mposhi": [28.682, -13.972],
  Mkushi: [29.3939, -13.6201],
  // Copperbelt (unchanged)
  Ndola: [28.6353, -12.973],
  Kitwe: [28.2132, -12.8024],
  Chingola: [27.8838, -12.5422],
  Mufulira: [28.2407, -12.5498],

  // Eastern
  Chipata: [32.6386, -13.6333],
  Katete: [32.04397, -14.05841],
  Petauke: [31.3197, -14.2412],

  // Luapula
  Mansa: [28.8958, -11.1997],
  Samfya: [29.5565, -11.3649],

  // Lusaka Province
  Lusaka: [28.3228, -15.3875],
  Chongwe: [28.68204, -15.32916],
  Kafue: [28.1815, -15.7776],

  // Muchinga
  Chinsali: [32.0816, -10.5414],
  Isoka: [32.6335, -10.1606],
  // Northern
  Kasama: [31.18, -10.2129],
  Mbala: [31.3659, -8.8402],

  // North-Western
  Solwezi: [26.3894, -12.1733],
  Zambezi: [23.1047, -13.5432],

  // Southern
  Livingstone: [25.8542, -17.8419],
  Choma: [26.9875, -16.8089],
  Mazabuka: [27.748, -15.856],

  // Western
  Mongu: [23.1274, -15.2484],
  Senanga: [23.2667, -16.1167],
};

const sampleLabData = [
  {
    labName: "laby",
    province: "Central",
    city: "Kapiri Mposhi",
    status: "Poor",
  },
  {
    labName: "labx",
    province: "Lusaka",
    city: "Lusaka",
    status: "Fair",
  },
  {
    labName: "labz",
    province: "Copperbelt",
    city: "Ndola",
    status: "Excellent",
  },
  {
    labName: "labw",
    province: "Luapula",
    city: "Mansa",
    status: "Good",
  },
];

const colorPalette = ["#E60101", "#1302FB", "#1CDF03", "#FE9900"];

const educationColorPalette = [
  "#C607CA",
  "#1302FB",
  "#1CDF03",
  "#FE9900",
  "#E60101",
];


const buildDashboardStatsFromLabs = (labs = []) => {
  const totalLabs = labs.length;

  const licensedLabs = labs.filter((l) => l.hpcz?.expiryDate).length;
  const unlicensedLabs = totalLabs - licensedLabs;

  const sliptaCertified = labs.filter((l) => l.slipta?.nextDue).length;

  const sadcasLevelA = labs.filter((l) => l.sadcas?.type === "SADCAS-A").length;
  const sadcasLevelB = labs.filter((l) => l.sadcas?.type === "SADCAS-B").length;

  return {
    totalLabs,
    licensedLabs,
    unlicensedLabs,
    sliptaCertified,
    sadcasLevelA,
    sadcasLevelB,
    staffData: [],       // charts disabled offline
    educationData: [],
    labStatusChartData: [],
  };
};

const Dashboard = () => {
  const { token } = useAuth();
  const [showLabsTable, setShowLabsTable] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showContent, setShowContent] = useState("accreditation");
  const [activeLab, setActiveLab] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    totalLabs: 0,
    licensedLabs: 0,
    unlicensedLabs: 0,
    sliptaCertified: 0,
    sadcasLevelA: 0,
    sadcasLevelB: 0,
    staffData: [],
    educationData: [],
    labStatusChartData: [],
  });

  const [labData, setLabData] = useState([]);
  const [labTableData, setLabTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionColumns = [
    { label: "Laboratory", field: "laboratoryName" },
    { label: "Location", field: "location" },
    { label: "Status", field: "status" },
    { label: "Reagent Stock", field: "reagentStock" },
    { label: "Equipment Status", field: "equipmentStatus" },
    { label: "Sample Backlog", field: "sampleBacklog" },
    { label: "SLIPTA", field: "slipta" },
    { label: "SADCAS", field: "sadcas" },
    { label: "HPCZ", field: "hpcz" },
    { label: "Staff", field: "staff" },
  ];

  const [filters, setFilters] = useState({
    province: "",
    city: "",
    search: "",
  });

  console.log("Filters:", labData);

  const labsWithCoords = useMemo(() => {
    return labData
      .map((lab) => {
        const coords = CITY_COORDS[lab.city];
        if (!coords) {
          console.warn(`No coordinates for city: ${lab.city}`);
          return null;
        }
        return { ...lab, coordinates: coords };
      })
      .filter(Boolean);
  }, [labData]);

  const filteredLabs = useMemo(() => {
    return labsWithCoords.filter((lab) => {
      const matchesProvince = filters.province
        ? lab.province === filters.province
        : true;
      const matchesCity = filters.city ? lab.city === filters.city : true;
      const matchesSearch = filters.search
        ? lab.labName.toLowerCase().includes(filters.search.toLowerCase())
        : true;

      return matchesProvince && matchesCity && matchesSearch;
    });
  }, [labsWithCoords, filters]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      try {
        // 🔹 1. Load cached labs FIRST
        const localLabs = await db.offlineLabs.toArray();

        if (localLabs.length > 0) {
          console.log("[Dashboard] Loaded labs from offline cache");

          setLabData(localLabs);

          setDashboardData(buildDashboardStatsFromLabs(localLabs));

          setLabTableData(
            localLabs.map((lab) => ({
              date: lab.createdAt
                ? new Date(lab.createdAt).toLocaleString()
                : "",
              laboratoryName: lab.labName || "",
              licenseNumber: lab.hpczLicense || "",
              location: (
                <>
                  {lab.province && (
                    <>
                      <IoLocationOutline /> {lab.province}
                    </>
                  )}
                  <br />
                  {lab.city}
                </>
              ),
              status: lab.status || "",
              phone: lab.telephone || "",
              email: lab.email || "",
              staff: lab.totalStaff || 0,
              slipta: lab.slipta || null,
              sadcas: lab.sadcas || null,
              hpcz: lab.hpcz || false,
              id: lab.id,
              latestWeeklyReport: lab.latestWeeklyReport || {},
            }))
          );
        }

        // 🔹 2. Stop here if offline
        if (!navigator.onLine) {
          // toast.info("Offline mode: showing cached dashboard data");
          return;
        }

        // 🔹 3. Online → fetch fresh dashboard stats
        const statusRes = await axios.get(`${env.kataApiUrl}/api/labs/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setDashboardData(statusRes.data);

        // 🔹 4. Fetch labs
        const labsRes = await axios.get(`${env.kataApiUrl}/api/labs`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setLabData(labsRes.data);

        // 🔹 5. Update offline cache safely
        for (const lab of labsRes.data) {
          const local = await db.offlineLabs.get(lab._id);

          if (local?.offline) continue; // do NOT overwrite offline edits

          await db.offlineLabs.put({
            ...lab,
            id: lab._id,
            offline: false,
            lastSynced: new Date().toISOString(),
            timestamp: Date.now(),
          });
        }
      } catch (err) {
        console.error("[Dashboard] Failed to fetch data:", err);
        toast.error("Failed to refresh dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const downloadAccreditationReport = async (labId, type) => {
    try {
      const response = await fetch(
        `${env.kataApiUrl}/api/accreditation/${type}/${labId}/download-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type.replace(/\s+/g, "_")}_${labId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const downloadCertificationReport = async (labId) => {
    try {
      const response = await fetch(
        `${env.kataApiUrl}/api/certification/${labId}/download-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certification_Report_${labId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const itemList = [
    {
      label: "Total Laboratories",
      numberText: dashboardData.totalLabs,
      statusText: `+2 this month`,
      icon: <BsBuilding size={20} color="var(--primary-color)" />,
      iconBackground: "rgba(19, 2, 251, 0.2)",
    },
    {
      label: "SLIPTA Certified",
      numberText: dashboardData.sliptaCertified,
      statusText: `${(
        (dashboardData.sliptaCertified / dashboardData.totalLabs) *
        100
      ).toFixed(1)}% of total`,
      icon: <LiaCertificateSolid size={20} color="#1CDF03" />,
      iconBackground: "rgba(28, 223, 3, 0.2)",
    },
    {
      label: "HPCZ Licensed",
      numberText: dashboardData.licensedLabs,
      statusText: `${(
        (dashboardData.licensedLabs / dashboardData.totalLabs) *
        100
      ).toFixed(1)}% of total`,
      icon: <AiOutlineSafetyCertificate size={20} color="#C607CA" />,
      iconBackground: "rgba(198, 7, 202, 0.2)",
    },
    {
      label: "Compliance Rate",
      numberText: `${
        dashboardData.totalLabs > 0
          ? (
              ((dashboardData.sliptaCertified +
                dashboardData.sadcasLevelA +
                dashboardData.sadcasLevelB) /
                dashboardData.totalLabs) *
              100
            ).toFixed(1)
          : 0
      }%`,
      statusText: `Based on SLIPTA & SADCAS`,
      icon: <FaArrowTrendUp size={20} color="#1CDF03" />,
      iconBackground: "rgba(28, 223, 3, 0.2)",
    },
  ];

  const itemStats = [
    {
      label: "SADCAS Level A",
      icon: <LiaCertificateSolid size={20} color="#1CDF03" />,
      color: "#1CDF03",
      iconBackground: "rgba(28, 223, 3, 0.2)",
      numberText: dashboardData.sadcasLevelA || 0,
      statusText: `${
        dashboardData.totalLabs
          ? (
              (dashboardData.sadcasLevelA / dashboardData.totalLabs) *
              100
            ).toFixed(1)
          : 0
      }% of total labs`,
    },
    {
      label: "SADCAS Level B",
      icon: <LiaCertificateSolid size={20} color="var(--primary-color)" />,
      color: "var(--primary-color)",
      iconBackground: "rgba(19, 2, 251, 0.2)",
      numberText: dashboardData.sadcasLevelB || 0,
      statusText: `${
        dashboardData.totalLabs
          ? (
              (dashboardData.sadcasLevelB / dashboardData.totalLabs) *
              100
            ).toFixed(1)
          : 0
      }% of total labs`,
    },
    {
      label: "Advanced Degrees",
      icon: <PiGraduationCap size={20} color="#C607CA" />,
      color: "#C607CA",
      iconBackground: "rgba(198, 7, 202, 0.2)",
      numberText: dashboardData.staffTotals
        ? dashboardData.staffTotals.degreeHolders +
            dashboardData.staffTotals.mastersHolders || 0
        : 0,
      statusText: "PhD + Masters holders",
    },
  ];

  const licensesStats = [
    {
      label: "Licensed Laboratories",
      icon: <GoShieldCheck size={40} color="#1CDF03" />,
      color: "#1CDF03",
      iconBackground: "rgba(28, 223, 3, 0.2)",
      numberText: labData
        .filter((lab) => lab.hpcz?.expiryDate)
        .length.toString(),
    },
    {
      label: "Unlicensed Laboratories",
      icon: <GoShieldX size={40} color="#BA3737" />,
      color: "#BA3737",
      iconBackground: "rgba(186, 55, 55, 0.2)",
      numberText: labData
        .filter((lab) => !lab.hpcz?.expiryDate)
        .length.toString(),
    },
  ];

  const accreditationStats = [
    {
      label: "SLIPTA Certified",
      icon: <LiaCertificateSolid size={40} color="#1CDF03" />,
      color: "#1CDF03",
      iconBackground: "rgba(28, 223, 3, 0.2)",
      numberText: dashboardData.sliptaCertified,
    },
    {
      label: "SADCAS Accredited",
      icon: (
        <AiOutlineSafetyCertificate size={40} color="var(--primary-color)" />
      ),
      color: "var(--primary-color)",
      iconBackground: "rgba(19, 2, 251, 0.2)",
      numberText: dashboardData.sadcasLevelA + dashboardData.sadcasLevelB,
    },
  ];

  const licenses = (
    <>
      <div className="row my-3">
        {licensesStats?.map((item, i) => (
          <div className="col-6" key={i}>
            <CommonCard
              className={`d-flex flex-column align-items-center gap-2 ${classes.certificationCard}`}
              style={{
                backgroundColor: item.iconBackground,
                borderColor: item.color,
                color: item.color,
              }}
            >
              <div>{item.icon}</div>
              <h4>{item.numberText}</h4>
              <p>{item.label}</p>
            </CommonCard>
          </div>
        ))}
      </div>

      <div className="d-flex flex-column gap-3 mt-4">
        <div className={`d-flex align-items-center gap-2`}>
          <GoShieldCheck size={25} color="#1CDF03" />
          <h4>Licensed Laboratories</h4>
        </div>

        {labData
          .filter((lab) => lab.hpcz?.expiryDate)
          .map((lab, i) => (
            <CommonCard
              className={`${classes.certificationCard} ${classes.licensedCard}`}
              key={i}
            >
              <div className={`d-flex justify-content-between`}>
                <div>
                  <h5>{lab.labName}</h5>
                  <p>
                    {lab.province}, {lab.city}
                  </p>
                </div>
                <div className="d-flex justify-content-center gap-2">
                  <div className={classes.sliptapill}>
                    <GoShieldCheck size={15} color="#1CDF03" /> Licensed
                  </div>
                </div>
              </div>
              <div className={`row ${classes.statusContainer}`}>
                <div className="col-6">
                  <p>
                    <span>Validity period: </span>{" "}
                    {lab.hpcz?.validityPeriod || "N/A"}
                  </p>
                  <p>
                    <span>Expires: </span>{" "}
                    {new Date(lab.hpcz?.expiryDate).toLocaleDateString() ||
                      "N/A"}
                  </p>
                </div>
                <div className="col-6">
                  <p>
                    <span>License Number:</span> {lab.hpczLicense || "N/A"}
                  </p>
                  <p>
                    <span>Status:</span>{" "}
                    <span className={classes.activePill}>Active</span>
                  </p>
                </div>
              </div>

              <div className="d-flex gap-4 mt-4">
                <div
                  className={classes.downloadButton}
                  role="button"
                  onClick={() => {
                    // Handle view license action
                    window.open(lab.fileUrl, "_blank");
                  }}
                >
                  View License
                </div>

                <a
                  href={lab.fileUrl}
                  download
                  target="_blank"
                  className={classes.downloadButton}
                >
                  Download Lab License
                </a>

                <a
                  href={lab.staffLicensesUrl}
                  download
                  target="_blank"
                  className={classes.downloadButton}
                >
                  Download Staff Licenses
                </a>
              </div>
            </CommonCard>
          ))}

        <div className={`d-flex align-items-center gap-2 mt-4`}>
          <GoShieldX size={25} color="#BA3737" />
          <h4>Unlicensed Laboratories</h4>
        </div>

        {labData
          .filter((lab) => !lab.hpcz?.expiryDate)
          .map((lab, i) => (
            <CommonCard
              className={`${classes.certificationCard} ${classes.unlicensedCard}`}
              key={i}
            >
              <div className={`d-flex justify-content-between`}>
                <div>
                  <h5>{lab.labName}</h5>
                  <p>
                    {lab.province}, {lab.city}
                  </p>
                </div>
                <div className="d-flex justify-content-center gap-2">
                  <div className={classes.unlicensedPill}>
                    <GoShieldX size={15} color="#BA3737" /> Not Licensed
                  </div>
                </div>
              </div>

              <p style={{ color: "#BA3737" }}>
                This laboratory requires HPCZ licensing to operate legally.
              </p>
            </CommonCard>
          ))}
      </div>
    </>
  );

  const accreditation = (
    <>
      <div className="row my-3">
        {accreditationStats?.map((item, i) => (
          <div className="col-6" key={i}>
            <CommonCard
              className={`d-flex flex-column align-items-center gap-2 ${classes.certificationCard}`}
              style={{
                backgroundColor: item.iconBackground,
                borderColor: item.color,
                color: item.color,
              }}
            >
              <div>{item.icon}</div>
              <h4>{item.numberText}</h4>
              <p>{item.label}</p>
            </CommonCard>
          </div>
        ))}
      </div>
      <div className="d-flex flex-column gap-3 mt-4">
        {labData
          .filter((lab) => lab.slipta?.nextDue || lab.sadcas?.type) // only certified labs
          .map((lab, i) => (
            <CommonCard className={classes.certificationCard} key={i}>
              <div className={`d-flex justify-content-between`}>
                <div>
                  <h5>{lab.labName}</h5>
                  <p>
                    {lab.province}, {lab.city}
                  </p>
                </div>
                <div className="d-flex justify-content-center gap-2">
                  {lab.slipta?.nextDue && (
                    <div className={classes.sliptapill}>SLIPTA</div>
                  )}
                  {lab.sadcas?.type && (
                    <div className={classes.sadcaspill}>{lab.sadcas.type}</div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-center gap-4">
                {lab.slipta?.nextDue && (
                  <div
                    className={classes.downloadButton}
                    role="button"
                    onClick={() => downloadCertificationReport(lab._id)}
                  >
                    Download SLIPTA Certificate Report
                  </div>
                )}
                {(lab.sadcas?.type === "SADCAS-A" ||
                  lab.sadcas?.type === "BOTH SADCAS A & B") && (
                  <div
                    className={classes.downloadButton}
                    role="button"
                    onClick={() =>
                      downloadAccreditationReport(lab._id, "SADCAS F134-A")
                    }
                  >
                    Download SADCAS A Report
                  </div>
                )}
                {(lab.sadcas?.type === "SADCAS-B" ||
                  lab.sadcas?.type === "BOTH SADCAS A & B") && (
                  <div
                    className={classes.downloadButton}
                    role="button"
                    onClick={() =>
                      downloadAccreditationReport(lab._id, "SADCAS F134-B")
                    }
                  >
                    Download SADCAS B Report
                  </div>
                )}
              </div>

              <div className={`${classes.statusContainer} mt-4`}>
                {lab.slipta && (
                  <p>
                    <span>SLIPTA Last Certified:</span>{" "}
                    {new Date(lab.slipta.lastCertified).toLocaleDateString()}
                  </p>
                )}
                {lab.sadcas && (
                  <p>
                    <span>SADCAS Last Assessment:</span>{" "}
                    {new Date(lab.sadcas.lastAssessment).toLocaleDateString()}
                  </p>
                )}
                {lab.status && (
                  <p>
                    <span>Status:</span>{" "}
                    <span
                      className={classes.activePill}
                      style={{
                        backgroundColor:
                          lab.status === "Poor"
                            ? "rgba(186, 55, 55, 0.2)"
                            : lab.status === "Fair"
                            ? "rgba(186, 125, 55, 0.2)"
                            : lab.status === "Good"
                            ? "rgba(19, 2, 251, 0.2)"
                            : "transparent", // fallback
                        color:
                          lab.status === "Poor"
                            ? "#BA3737"
                            : lab.status === "Fair"
                            ? "#BA7D37"
                            : lab.status === "Good"
                            ? "var(--primary-color)"
                            : "#000",
                      }}
                    >
                      {lab.status}
                    </span>
                  </p>
                )}
              </div>
            </CommonCard>
          ))}
      </div>
    </>
  );

  return (
    <div className={classes.wrapper}>
      {showPopup && (
        <OverlayModal
          onClose={() => setShowPopup(false)}
          title={
            showContent === "licenses"
              ? "HPCZ License Viewer"
              : "Accreditation & Certification Reports"
          }
        >
          {showContent === "licenses" ? licenses : accreditation}
        </OverlayModal>
      )}
      <h2>Dashboard</h2>

      <div className="row mt-3">
        {itemList?.map((item, i) => (
          <div className="col-3" key={i}>
            <CommonCard className={classes.certificationCard}>
              <div className="row">
                {/* Labels */}
                <div className={`${classes.labelsContainer}`}>
                  <div
                    className={`d-flex align-items-center ${classes.certificationCardHeader} mb-2`}
                  >
                    <h4>{item.label}</h4>
                  </div>
                  <div
                    className={`d-flex align-items-center justify-content-between  mb-2`}
                  >
                    <h2 className={classes.numberText}>{item.numberText}</h2>
                    <div
                      className={`d-flex align-items-center justify-content-center ${classes.iconCard}`}
                      style={{
                        backgroundColor: item.iconBackground,
                      }}
                    >
                      {item.icon}
                    </div>
                  </div>
                  <h5>{item.statusText}</h5>
                </div>
              </div>
              {item.downloadButton && (
                <div className={classes.downloadButton} role="button">
                  {item.downloadButton.text}
                </div>
              )}
            </CommonCard>
          </div>
        ))}
      </div>

      <CommonCard className={`my-4 ${classes.quickActionCard}`}>
        <div className={`d-flex align-items-center justify-content-between`}>
          <h3 className="m-0">Quick Actions</h3>
          <div className={`d-flex align-items-center gap-3`}>
            <CommonButton
              title={
                showLabsTable ? "Hide Lab Directory" : "Show Lab Directory"
              }
              icon={<IoListOutline style={{ marginRight: 5, flexShrink: 0 }} />}
              className={classes.customButton}
              onClick={() => setShowLabsTable(!showLabsTable)}
            />
            <CommonButton
              title="Download Accreditation Reports"
              icon={
                <HiOutlineDownload
                  size={20}
                  style={{ marginRight: 5, flexShrink: 0 }}
                />
              }
              onClick={() => {
                setShowPopup(true);
                setShowContent("accreditation");
              }}
              className={classes.customButton}
            />
            <CommonButton
              title="View HPCZ Licenses"
              icon={
                <AiOutlineSafetyCertificate
                  size={20}
                  style={{ marginRight: 5, flexShrink: 0 }}
                />
              }
              onClick={() => {
                setShowPopup(true);
                setShowContent("licenses");
              }}
              className={classes.customButton}
            />
          </div>
        </div>
      </CommonCard>
      {showLabsTable && (
        <CommonCard className={classes.labsCard}>
          <ReusableTable
            title="KATA Laboratory Directory"
            columns={sectionColumns}
            data={loading ? [] : labTableData}
            detailsLink="lab-details"
            className={classes.labTable}
            showActions={false}
          />
        </CommonCard>
      )}
      <div className="row mt-3">
        {itemStats?.map((item, i) => (
          <div className="col-4" key={i}>
            <CommonCard
              className={classes.certificationCard}
              style={{
                backgroundColor: item.iconBackground,
                borderColor: item.color,
              }}
            >
              <div className="row">
                {/* Labels */}
                <div className={`${classes.labelsContainer}`}>
                  <div
                    className={`d-flex align-items-center ${classes.certificationCardHeader} mb-2`}
                  >
                    <h4 style={{ color: item.color }}>{item.label}</h4>
                  </div>
                  <div
                    className={`d-flex align-items-center justify-content-between  mb-2`}
                  >
                    <h2
                      className={classes.numberText}
                      style={{ color: item.color }}
                    >
                      {item.numberText}
                    </h2>
                    <div
                      className={`d-flex align-items-center justify-content-center ${classes.iconCard}`}
                      style={{
                        backgroundColor: item.iconBackground,
                      }}
                    >
                      {item.icon}
                    </div>
                  </div>
                  <h5 style={{ color: item.color }}>{item.statusText}</h5>
                </div>
              </div>
              {item.downloadButton && (
                <div className={classes.downloadButton} role="button">
                  {item.downloadButton.text}
                </div>
              )}
            </CommonCard>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <CommonCard className="p-3 mb-4">
          <h2 className="m-0">Search & Filter Laboratories</h2>

          <div className="row mt-3">
            {/* Province Filter */}
            <div className="col-4">
              <InputField
                labelText="Province"
                placeholder="Select province"
                dropdownOptions={[
                  { value: "", label: "All" },
                  ...[...new Set(labData.map((l) => l.province))].map(
                    (prov) => ({
                      value: prov,
                      label: prov,
                    })
                  ),
                ]}
                value={filters.province}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, province: e.target.value }))
                }
                dropdownClass="w-100"
              />
            </div>
            {/* City Filter */}
            <div className="col-4">
              <InputField
                labelText="City"
                placeholder="Select city"
                dropdownOptions={[
                  { value: "", label: "All" },
                  ...[...new Set(labData.map((l) => l.city))].map((city) => ({
                    value: city,
                    label: city,
                  })),
                ]}
                value={filters.city}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, city: e.target.value }))
                }
                dropdownClass="w-100"
              />
            </div>
            {/* Search by Lab Name */}
            <div className="col-4">
              <InputField
                labelText="Search Lab"
                placeholder="Enter lab name..."
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </div>
          </div>
        </CommonCard>

        <div className="row">
          <div className="col-8">
            <CommonCard className="p-3">
              <ZambiaLabsMap
                filteredLabs={filteredLabs}
                STATUS_COLORS={STATUS_COLORS}
                activeLab={activeLab}
                setActiveLab={setActiveLab}
              />
            </CommonCard>
          </div>
          <div className="col-4">
            <CommonCard>
              <div className={classes.cardHeader}>
                {activeLab ? (
                  <SelectedLabDetails
                    lab={activeLab}
                    onClose={() => setActiveLab(null)}
                    STATUS_COLORS={STATUS_COLORS}
                  />
                ) : (
                  <LabStatusOverview
                    STATUS_COLORS={STATUS_COLORS}
                    labData={labData}
                  />
                )}
              </div>
            </CommonCard>
          </div>
        </div>
      </div>
      <div className="row my-4">
        <div className="col-4">
          <CommonCard>
            <div className={classes.cardHeader}>
              <h5>Certification & Accreditation</h5>
            </div>
            <LabStatusChart dashboardData={dashboardData} />
          </CommonCard>
        </div>

        <div className="col-8">
          <CommonCard>
            <div className={classes.cardHeader}>
              <h5>Professional Breakdown</h5>
            </div>
            <CustomBarChart
              data={dashboardData.staffData}
              colors={colorPalette}
            />
          </CommonCard>
          <CommonCard className="mt-4">
            <div className={classes.cardHeader}>
              <h5>Education Levels</h5>
            </div>
            <CustomBarChart
              data={dashboardData.educationData}
              colors={educationColorPalette}
            />
          </CommonCard>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
